/* Typed hooks over the API boundary.

   Components never call `api.*` directly. Everything goes through a hook here
   so the cache key, the stale policy and the invalidation set for an operation
   live next to each other and cannot drift apart. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from './api'
import { qk, queryClient } from './queryClient'
import { registerAreaNames, registerCities } from './geo'
import { usePrefs } from '../state/prefs'
import type { Locale } from './format'
import { HexApiError, type Address, type ProviderFilters, type TimeSlot } from './types'

/* ── catalog ──────────────────────────────────────────────────────────────── */

export function useCategories() {
  return useQuery({
    queryKey: qk.categories,
    queryFn: ({ signal }) => api.getCategories(signal),
    // The category list is effectively static for the life of a session.
    staleTime: Infinity,
  })
}

export function useAreas() {
  return useQuery({
    queryKey: qk.areas,
    queryFn: async ({ signal }) => {
      const areas = await api.getAreas(signal)
      /* The server owns what these districts are called, and `districtLabel` is
         reached from `addressLine` — a pure formatter with no access to a query.
         Recording the names here is what lets a receipt and an address line
         render an area this build has never heard of in the right script. */
      registerAreaNames(areas)
      return areas
    },
    staleTime: Infinity,
  })
}

/** Keeps the customer's chosen district honest against what the API serves.
 *
 *  Two jobs, both of which used to be a compiled constant's problem:
 *
 *  · **No choice yet** — a first visit, or a country change. The district
 *    defaults to the first the server serves rather than one named in this
 *    bundle, because a bundled default is a guess about the server's data.
 *  · **A choice that has stopped being served** — the district was switched
 *    off, or its whole market was. That is now an ordinary operator action, so
 *    it is an ordinary state to be in: the app moves the visitor to a served
 *    district instead of leaving them on one where every list is empty and
 *    nothing on screen says why.
 *
 *  Called once, from `App`. It writes through `setArea`, so the choice persists
 *  exactly as a deliberate one does. */
export function useResolvedArea(): void {
  const { area, setArea } = usePrefs()
  const { data: areas } = useAreas()

  useEffect(() => {
    if (!areas || areas.length === 0) return
    if (areas.some((a) => a.id === area)) return
    setArea(areas[0]!.id)
  }, [areas, area, setArea])
}

/** The markets HexClean is open in.
 *
 *  Its own query rather than something derived from the areas, because the
 *  "we serve …" line has to name a market that may have no districts on file
 *  yet, and a heading order taken from whichever district happens to mention a
 *  city first is not an order anybody chose. */
export function useCities() {
  return useQuery({
    queryKey: qk.cities,
    queryFn: async ({ signal }) => {
      const cities = await api.getCities(signal)
      registerCities(cities)
      return cities
    },
    staleTime: Infinity,
  })
}

/** Landing-page social proof. Static for the session, like the catalog. */
export function useFeaturedReviews(limit = 10) {
  return useQuery({
    queryKey: qk.featuredReviews,
    queryFn: ({ signal }) => api.getFeaturedReviews(limit, signal),
    staleTime: Infinity,
  })
}

export function useProviders(filters: ProviderFilters) {
  return useQuery({
    queryKey: qk.providers(filters),
    queryFn: ({ signal }) => api.getProviders(filters, signal),
    /* Keeps the previous list on screen while a filter change loads. Without
       it, every tap on a sort chip empties the page to a spinner and then
       refills it — the content flashing out is what reads as slow, not the
       request. */
    placeholderData: (prev) => prev,
  })
}

/** By slug — from a URL — or by id, which is what a basket and an order carry.
 *
 *  The two key the cache separately, so a provider reached both ways is fetched
 *  twice in a session. That is the price of not making every screen holding an
 *  id find a slug first, and the only client-side table that could do that
 *  translation was the seed catalog. One redundant cached read beats four
 *  screens that render seed data against a live backend. */
export function useProvider(key: string | undefined) {
  return useQuery({
    queryKey: qk.provider(key ?? ''),
    queryFn: ({ signal }) => api.getProvider(key!, signal),
    enabled: Boolean(key),
  })
}

/** Codes on offer, for the home screen's rail. Static for the session. */
export function usePromos() {
  return useQuery({
    queryKey: qk.promos,
    queryFn: ({ signal }) => api.getPromos(signal),
    staleTime: Infinity,
  })
}

/** A price list by id: a category for HexClean's own, a provider for a
 *  partner's. Every caller downstream of the basket uses this one hook and
 *  never learns which it got. */
export function useMenu(listId: string | undefined) {
  return useQuery({
    queryKey: qk.menu(listId ?? ''),
    queryFn: ({ signal }) => api.getMenu(listId!, signal),
    enabled: Boolean(listId),
  })
}

export function useReviews(providerId: string | undefined) {
  return useQuery({
    queryKey: qk.reviews(providerId ?? ''),
    queryFn: ({ signal }) => api.getReviews(providerId!, signal),
    enabled: Boolean(providerId),
  })
}

export function useSlots(listId: string | undefined) {
  return useQuery({
    queryKey: qk.slots(listId ?? ''),
    queryFn: ({ signal }) => api.getSlots(listId!, 7, signal),
    enabled: Boolean(listId),
    // Availability is the one catalog read that genuinely goes stale while you
    // look at it — someone else can take the 4pm slot mid-checkout.
    staleTime: 60_000,
  })
}

/** The half of a quote request that decides the price.
 *
 *  This is the cache key for `useQuote`, and it is a projection rather than the
 *  request itself because the two differ in both directions and each direction
 *  costs something. A `CartLine` carries `key`, `listId` and `notes`, none of
 *  which reach `domain.Price` — keying on them re-prices the basket because
 *  somebody typed "no starch". A `TimeSlot` carries `available`, `surcharge`
 *  and `to`, which the server recomputes from `date` and `from` against the
 *  category's trading hours; keying on a surcharge this app worked out means a
 *  cached price that moves when the client's guess does.
 *
 *  What is left is exactly what `domain.QuoteInput` reads, which is the point:
 *  two baskets that key the same are two baskets the service prices the same. */
export function pricedFields(input: api.QuoteInput) {
  return {
    listId: input.listId,
    venue: input.venue,
    lines: input.lines.map((l) => ({ itemId: l.itemId, qty: l.qty, optionIds: l.optionIds })),
    slot: input.slot && { date: input.slot.date, from: input.slot.from },
    promoCode: input.promoCode,
    useWallet: input.useWallet,
    providerId: input.providerId,
  }
}

/** What the basket costs, decided by the side that will charge for it.
 *
 *  `local.ts#priceOrder` is a copy of `domain.Price` and it agrees with it
 *  today — but it agrees by having been realigned twice, not by construction,
 *  and it cannot ever agree about the things it is not told: whether a code is
 *  still active, how much of its global cap is left, how many times *this*
 *  customer has redeemed it. Those live behind the route, so the number a
 *  customer is charged comes from the route.
 *
 *  `priceOrder` stays for the first frame, and it stays as the whole of mock
 *  mode, where it genuinely is the server.
 *
 *  Not cached the way the catalogue is. A quote is a statement about a basket
 *  *now* — a code can be exhausted and a wallet spent from another tab between
 *  two renders — so this always revalidates, and `usePlaceOrder`'s caller
 *  re-reads it once more before charging anybody. */
export function useQuote(input: api.QuoteInput | null) {
  return useQuery({
    queryKey: qk.quote(input && pricedFields(input)),
    queryFn: ({ signal }) => api.getQuote(input!, signal),
    enabled: Boolean(input),
    staleTime: 0,
    /* Keeps the last price on screen while the next one loads. Without it every
       change of venue or window drops the summary back to the local estimate
       and then to the server's, so the total visibly changes twice for one
       decision. */
    placeholderData: (prev) => prev,
  })
}

/** Reserves the chosen window for as long as checkout takes.
 *
 *  Capacity is per window and small, so without this two customers who open
 *  checkout at the same moment both see 16:00 free, both book it, and one of
 *  them finds out at 16:00. The hold makes the loser find out while they are
 *  still looking at a picker they can choose again from.
 *
 *  Not a `useMutation`, because a hold is a lifecycle rather than an action:
 *  it is taken when a window becomes the chosen one and given back when it
 *  stops being — including when the screen unmounts, which no button press
 *  corresponds to.
 *
 *  Failure is deliberately quiet except for the one case the customer can act
 *  on. A hold that could not be taken for any other reason leaves `holdId`
 *  null, placement sends none, and the service checks the window itself — the
 *  same outcome as before any of this existed. `conflict` is the exception:
 *  that one means the window has gone, and saying nothing would leave them
 *  pressing a button that cannot work.
 *
 *  Signed-in only. The route carries the `customer` audience, so calling it
 *  without a session is a 401 — which this app's transport answers by trying
 *  to refresh and then clearing the session. An anonymous visitor reaching
 *  checkout must not be signed out by a background reservation. */
export function useSlotHold(listId: string | undefined, slot: TimeSlot | null, enabled: boolean) {
  const [holdId, setHoldId] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  /* Set once an order has consumed the hold, so the cleanup below leaves it
     alone. Spending and releasing are different things: the order occupies the
     window now, and `ConsumeHold` marks the record rather than deleting it
     precisely so the sweeper can tell a hold that lapsed from one that was
     used. Releasing it afterwards throws that away and spends a request to do
     it. A ref rather than state because nothing renders from it and the
     cleanup has to see the current value, not the one from its own render. */
  const spent = useRef(false)

  /* The window's identity, not the object's. `TimeSlot` carries `available`
     and `surcharge` as well, and a re-fetched slot list hands back a new object
     for the same window — holding on identity would release and re-take the
     reservation every time availability refreshed. */
  const date = slot?.date ?? null
  const from = slot?.from ?? null

  useEffect(() => {
    if (!enabled || !listId || !date || !from) {
      setHoldId(null)
      return
    }

    /* Whether this effect is still the current one. An answer that arrives
       after the customer has moved on belongs to a window they no longer want,
       so it is released rather than kept — otherwise a fast change of mind
       leaks a reservation for ten minutes. */
    let live = true
    let taken: string | null = null
    spent.current = false
    setConflict(false)

    void api
      .holdSlot({ listId, date, from, kind: 'delivery' })
      .then((hold) => {
        if (!live) {
          void api.releaseSlot(hold.holdId).catch(() => {})
          return
        }
        taken = hold.holdId
        setHoldId(hold.holdId)
      })
      .catch((err: unknown) => {
        if (!live) return
        setHoldId(null)
        if (err instanceof HexApiError && err.code === 'conflict') setConflict(true)
      })

    return () => {
      live = false
      setHoldId(null)
      /* Best effort, and swallowed on purpose. The service sweeps lapsed holds
         on a timer because a closed tab never gets here at all, so the cost of
         this failing is ten minutes of one window's capacity — not something to
         interrupt a customer over. */
      if (taken && !spent.current) void api.releaseSlot(taken).catch(() => {})
    }
  }, [listId, date, from, enabled])

  /** Called once an order has consumed the hold. Checkout unmounts on the
   *  navigation that follows a successful placement, and without this the
   *  cleanup would hand back a window the new order is sitting in. */
  const spend = useCallback(() => {
    spent.current = true
  }, [])

  return { holdId, conflict, spend }
}

export function useSearch(query: string, locale: Locale) {
  return useQuery({
    queryKey: qk.search(query, locale),
    queryFn: ({ signal }) => api.search(query, locale, signal),
    enabled: query.trim().length >= 2,
    staleTime: 2 * 60_000,
  })
}

/** Warm a provider's page before the user commits to opening it. Called from
 *  the card's pointerenter/touchstart, which buys 200–400ms of head start on
 *  a request that would otherwise begin after the route transition. */
export function prefetchProvider(slug: string) {
  void queryClient.prefetchQuery({
    queryKey: qk.provider(slug),
    queryFn: ({ signal }) => api.getProvider(slug, signal),
  })
}

/* ── addresses ────────────────────────────────────────────────────────────── */

export function useAddresses() {
  return useQuery({
    queryKey: qk.addresses,
    queryFn: ({ signal }) => api.getAddresses(signal),
    staleTime: 60_000,
  })
}

export function useSaveAddress() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Omit<Address, 'id'> & { id?: string }) => api.saveAddress(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.addresses }),
  })
}

export function useDeleteAddress() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.deleteAddress(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.addresses }),
  })
}

/* ── favourites ─────────────────────────────────────────────────────────────
   Gone. Favourites were saved *shops*, and shops are no longer something the
   customer browses, compares or returns to: HexClean publishes a price per
   service and assigns the partner. The one place a partner is named is inside a
   drive-in booking, where the customer needs the address to drive to — and
   "save this forecourt for later" is not a thing that screen can honestly
   offer, because the next booking re-ranks by proximity anyway.

   `GET /v1/favourites` was also a third of the 401 burst the logged-out home
   screen fired (Live-Test P3-1); deleting the caller is the fix. */

/* ── orders ───────────────────────────────────────────────────────────────── */

/** Re-reads the signed-in customer.
 *
 *  `state/auth` hydrates the user from localStorage so the first frame paints
 *  with a name rather than flickering through "Sign in", and that copy is
 *  correct exactly until something changes it: an order settles and spends
 *  wallet credit, a delivery earns points, support adjusts a balance. All three
 *  numbers live on `User`, so without this the Account screen shows whatever
 *  was true at sign-in until the customer signs in again.
 *
 *  Gated on being signed in rather than firing and getting a 401 — an
 *  unauthenticated 401 here would spend the refresh cookie trying to rescue a
 *  session that was never started. */
export function useMe(enabled: boolean) {
  return useQuery({
    queryKey: qk.me,
    queryFn: ({ signal }) => api.getMe(signal),
    enabled,
    staleTime: 30_000,
  })
}

/** The signed-in customer's orders.
 *
 *  Gated on being signed in, same as `useMe` and for the same reason. A
 *  logged-out home screen used to fire this and get a 401 for its trouble —
 *  one third of the burst in Live-Test P3-1 — and a 401 here is not free: it
 *  spends the `__Host-` refresh cookie trying to rescue a session that was
 *  never started, so the noise costs a round trip *and* muddies the signal for
 *  a refresh that actually matters.
 *
 *  The default is `true` so the screens that only ever render behind an auth
 *  wall do not each have to remember to pass it. */
export function useOrders(enabled = true) {
  return useQuery({
    queryKey: qk.orders,
    queryFn: ({ signal }) => api.getOrders(signal),
    enabled,
    staleTime: 30_000,
  })
}

export function useOrder(id: string | undefined, live = false) {
  return useQuery({
    queryKey: qk.order(id ?? ''),
    queryFn: ({ signal }) => api.getOrder(id!, signal),
    enabled: Boolean(id),
    /* The tracking screen polls; the history list does not. Polling a
       delivered order forever is a battery cost with no information in it, so
       the interval switches itself off once the order reaches a terminal
       state. */
    refetchInterval: live
      ? (query) => {
          const status = query.state.data?.status
          return status === 'delivered' || status === 'cancelled' ? false : 15_000
        }
      : false,
  })
}

export function usePlaceOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: api.PlaceOrderInput) => api.placeOrder(input),
    onSuccess: (order) => {
      qc.setQueryData(qk.order(order.id), order)
      void qc.invalidateQueries({ queryKey: qk.orders })
    },
  })
}

export function useCancelOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.cancelOrder(id),
    onSuccess: (order) => {
      qc.setQueryData(qk.order(order.id), order)
      void qc.invalidateQueries({ queryKey: qk.orders })
    },
  })
}

export function useRateOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, stars }: { id: string; stars: number }) => api.rateOrder(id, stars),
    /* Keyed off the id that was rated, not off anything in the response. The
       API answers with the *review*, so reading `.id` from it would cache a
       review under a review's id and leave the order the customer is looking
       at untouched. Invalidated rather than written, because the order the
       server now holds carries more than the star that was just sent. */
    onSuccess: (_review, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.order(id) })
      void qc.invalidateQueries({ queryKey: qk.orders })
      /* The partner's score moved, so every list that ranks by it is stale.
         The bare prefix rather than `qk.providers(filters)`: this has to reach
         every cached filter combination, not the one nobody is looking at. */
      void qc.invalidateQueries({ queryKey: ['providers'] })
    },
  })
}

/** Drives the tracking page's simulated courier, in the builds that have one.
 *
 *  `canAdvanceOrder` is false against a real service and the tracking page
 *  does not start the interval at all — progress there comes from the partner
 *  app reporting in, which this app sees by polling `getOrder`. */
export function useAdvanceOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.advanceOrder(id),
    onSuccess: (order) => {
      qc.setQueryData(qk.order(order.id), order)
      void qc.invalidateQueries({ queryKey: qk.orders })
    },
  })
}

/* ── joining as a partner ─────────────────────────────────────────────────────
   There is no `useSubmitApplication` here any more, and its absence is the
   point rather than an oversight.

   This site carried a complete second application form at `/join/:kind`,
   posting to the same public `POST /applications` the partner app posts to. Two
   forms against one endpoint is two things to keep in step with a server that
   moves, and they were not in step: only the partner app's flow could send the
   documents behind an application — the upload endpoint is keyed by a reference
   this site threw away on unmount — and only that flow could set the password
   that lets an applicant come back and see what happened. Somebody who applied
   from here got a reference, an email asking for their ID, and nowhere to send
   it.

   `/join` still sells the proposition and `/join/:kind` still exists, as a
   redirect that carries the chosen shape across. The form itself belongs to the
   app that owns the rest of a partner's life. `api.submitApplication` is left
   on the contract because that file mirrors what the API serves rather than
   what this site calls; nothing here calls it. */
